import _                                                              from 'underscore';
import React, { useReducer }                                          from 'react';
import produce, { enablePatches, applyPatches, setAutoFreeze, Patch } from 'immer';
import { Uuid }                                                       from '../types';

setAutoFreeze( false );
enablePatches();

enum ACTIONS {
	SET_STATE = 'setState',
	UPDATE    = 'update',
	RESET     = 'reset',
	UNDO      = 'undo',
}

enum OPERATIONS {
	ADD     = 'add',
	REMOVE  = 'remove',
	REPLACE = 'replace',
};

interface UsePatchStateHookActionInput {
	type: ACTIONS,
	value?: any,
	stateFragment?: string,
	operation?: OPERATIONS,
}

interface UsePatchStateHookActions {
	setState( stateFragment: string, value: any, operation: OPERATIONS ): void,
	
	update( value: object ): void,
	
	reset(): void,
	
	undo(): void,
}

interface UsePatchStateHookState {

}

interface UsePatchStateHookPatchState extends UsePatchStateHookState {
	_patches: Patch[],
	_inversePatches: Patch[],
}

type UsePatchStateHook = ( initialState: UsePatchStateHookState ) => [ state: UsePatchStateHookPatchState, actions: UsePatchStateHookActions ];

export const usePatchState: UsePatchStateHook = ( initialState = {} ) => {
	
	let state: UsePatchStateHookPatchState = {
		...initialState,
		_patches:        [],
		_inversePatches: [],
	};
	
	let actionReducer = function reducer( state: UsePatchStateHookPatchState, action: UsePatchStateHookActionInput ){
		
		switch( action.type ){
			
			case ACTIONS.SET_STATE:
				return produce( state, draft => {
					
					if( !draft[ action.stateFragment ] ) return state;
					
					let isObject  = _.isObject( draft[ action.stateFragment ] ) && _.isUndefined( draft[ action.stateFragment ].length ),
					    isArray   = _.isArray( draft[ action.stateFragment ] ) && !_.isUndefined( draft[ action.stateFragment ].length ),
					    operation = action.operation;
					
					if( !isArray && !isObject ){
						operation = OPERATIONS.REPLACE;
					}
					
					switch( operation ){
						case OPERATIONS.ADD:
							draft[ action.stateFragment ] = isObject
							                                ? { ...draft[ action.stateFragment ], [ action.stateFragment ]: action.value }
							                                : [ ...draft[ action.stateFragment ], action.value ];
							break;
						case OPERATIONS.REMOVE:
							draft[ action.stateFragment ] = isObject
							                                ? _.omit( draft[ action.stateFragment ], action.stateFragment )
							                                : _.without( draft[ action.stateFragment ], action.value );
							break;
						case OPERATIONS.REPLACE:
						default:
							draft[ action.stateFragment ] = action.value;
							break;
					}
					
				}, ( patches, inversePatches ) => {
					
					state._patches.push( ...patches );
					state._inversePatches.push( ...inversePatches );
					
				} );
			case ACTIONS.UPDATE:
				return produce( state, draft => {
					
					_.mapObject( action.value, ( value, stateFragment ) => draft[ stateFragment ] = value );
					
				}, ( patches, inversePatches ) => {
					
					state._patches.push( ...patches );
					state._inversePatches.push( ...inversePatches );
					
				} );
			case ACTIONS.RESET:
				return produce( state, draft => {
					
					return {
						...initialState,
						_patches:        [],
						_inversePatches: [],
					};
					
				} );
			case ACTIONS.UNDO:
				return produce( state, draft => {
					
					const undo = _.last( state._inversePatches );
					
					if( !undo ) return state;
					
					return {
						...applyPatches( state, [ undo ] ),
						_inversePatches: _.initial( draft._inversePatches ),
						_patches:        _.rest( draft._patches ),
					};
					
				} );
			default:
				throw new Error();
		}
	};
	
	const [ _state, _dispatch ] = useReducer( actionReducer, state );
	
	return [
		_state,
		{
			[ ACTIONS.SET_STATE ]: ( stateFragment = null, value = null, operation = OPERATIONS.REPLACE ) => _dispatch( { type: ACTIONS.SET_STATE, stateFragment, value, operation } ),
			[ ACTIONS.UPDATE ]:    ( value = {} ) => _dispatch( { type: ACTIONS.UPDATE, value } ),
			[ ACTIONS.RESET ]:     () => _dispatch( { type: ACTIONS.RESET } ),
			[ ACTIONS.UNDO ]:      () => _dispatch( { type: ACTIONS.UNDO } ),
		}
	]
};
